import html2canvas from 'html2canvas';

/**
 * Take a screenshot of the current page or a specific element
 * Works on Chrome, Safari, Firefox
 *
 * @param {Object} options
 * @param {HTMLElement} options.element - Element to capture (default: document.body)
 * @param {string} options.filename - Download filename (default: 'screenshot.png')
 * @param {boolean} options.download - Auto-download the image (default: true)
 * @param {number} options.scale - Image scale/quality (default: 2 for retina)
 * @returns {Promise<string>} - Data URL of the image
 */
export const takeScreenshot = async ({
  element = document.body,
  filename = 'screenshot.png',
  download = true,
  scale = 2
} = {}) => {
  try {
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: getComputedStyle(document.body).backgroundColor || '#000',
      logging: false
    });

    const dataUrl = canvas.toDataURL('image/png');

    if (download) {
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    }

    return dataUrl;
  } catch (error) {
    console.error('Screenshot failed:', error);
    throw error;
  }
};

/**
 * Take a screenshot of an element by selector
 *
 * @param {string} selector - CSS selector
 * @param {Object} options - Same as takeScreenshot options
 */
export const screenshotElement = async (selector, options = {}) => {
  const element = document.querySelector(selector);
  if (!element) {
    throw new Error(`Element not found: ${selector}`);
  }
  return takeScreenshot({ ...options, element });
};

// For quick console use: window.screenshot()
if (typeof window !== 'undefined') {
  window.screenshot = takeScreenshot;
  window.screenshotElement = screenshotElement;
}
